import React, {useEffect, useState} from 'react';
import {ActionButton, Dropdown, IconButton, IDropdownOption, Label, Stack, Text, TextField, Toggle} from '@fluentui/react';
import {PRODUCT_LEVELS} from '../../../Constants';

// Product levels a global template can be restricted to.
const PRODUCT_LEVEL_OPTIONS: IDropdownOption[] = PRODUCT_LEVELS.map(level => ({key: level, text: level}));

export interface ITemplateVisibilityValue {
    isSystem: boolean;
    organizations: string[];
    productLevel: string[];
    entitlements: string[];
}

interface ITemplateVisibilityFieldsProps {
    value: ITemplateVisibilityValue;
    onChange: (partial: Partial<ITemplateVisibilityValue>) => void;
    disabled?: boolean;
}

const cleanRows = (rows: string[]): string[] => rows.map(v => v.trim()).filter(v => v.length > 0);

interface IStringListInputProps {
    label: string;
    description?: string;
    addButtonText: string;
    values: string[];
    disabled?: boolean;
    onChange: (values: string[]) => void;
}

// A list of single-value text inputs with a per-row remove button and an add-row button. Keeps
// blank rows locally (so a freshly added row isn't stripped out from under the user) while only
// reporting trimmed, non-empty values upward. Only resets from props when the incoming list
// actually differs from what the current rows clean to, so typing isn't fought by the round trip.
const StringListInput: React.FC<IStringListInputProps> = (props) => {
    const {values, onChange, disabled} = props;
    const [rows, setRows] = useState<string[]>(values);

    useEffect(() => {
        setRows(prev => (cleanRows(prev).join('\n') === values.join('\n') ? prev : values));
    }, [values]);

    const update = (next: string[]) => {
        setRows(next);
        onChange(cleanRows(next));
    };

    return (
        <Stack tokens={{childrenGap: 4}}>
            <Label disabled={disabled}>{props.label}</Label>
            {props.description && <Text variant="small">{props.description}</Text>}
            {rows.map((row, index) => (
                <Stack horizontal key={index} verticalAlign="center" tokens={{childrenGap: 4}}>
                    <Stack.Item grow>
                        <TextField
                            value={row}
                            disabled={disabled}
                            onChange={(_, v) => update(rows.map((r, i) => (i === index ? (v ?? '') : r)))}
                        />
                    </Stack.Item>
                    <IconButton
                        iconProps={{iconName: 'Delete'}}
                        title="Remove"
                        ariaLabel={`Remove ${props.label} entry`}
                        disabled={disabled}
                        onClick={() => update(rows.filter((_, i) => i !== index))}
                    />
                </Stack>
            ))}
            <div>
                <ActionButton
                    iconProps={{iconName: 'Add'}}
                    disabled={disabled}
                    onClick={() => setRows([...rows, ''])}
                >
                    {props.addButtonText}
                </ActionButton>
            </div>
        </Stack>
    );
};

// Shared "who can see this template" editor used by both the save and edit template modals.
// Callers are responsible for permission gating (useCan(STOREFRONT_TEMPLATES_GLOBAL)); this component
// only renders the fields.
const TemplateVisibilityFields: React.FC<ITemplateVisibilityFieldsProps> = (props) => {
    const {value, onChange, disabled} = props;

    return (
        <Stack tokens={{childrenGap: 12}}>
            <Toggle
                label={(
                    <Stack>
                        <Text>Publish as global template</Text>
                        <Text variant="small">Visible to other organizations</Text>
                    </Stack>
                )}
                checked={value.isSystem}
                disabled={disabled}
                onText="On"
                offText="Off"
                onChange={(_, checked) => onChange({isSystem: !!checked})}
            />

            {value.isSystem && (
                <Stack tokens={{childrenGap: 12}}>
                    <Stack tokens={{childrenGap: 4}}>
                        <Text variant="mediumPlus">Template Filters</Text>
                        <Text variant="small">
                            Restrict which organizations see this global template. Leave blank for all.
                        </Text>
                    </Stack>
                    <Dropdown
                        label="Product Levels"
                        multiSelect
                        options={PRODUCT_LEVEL_OPTIONS}
                        selectedKeys={value.productLevel}
                        disabled={disabled}
                        placeholder="All product levels"
                        onChange={(_, option) => {
                            if (!option) return;
                            const key = option.key as string;
                            const next = option.selected
                                ? [...value.productLevel, key]
                                : value.productLevel.filter(v => v !== key);
                            onChange({productLevel: next});
                        }}
                    />
                    <StringListInput
                        label="Organizations"
                        description="Organization IDs allowed to see this template. Blank = all organizations."
                        addButtonText="Add Organization"
                        values={value.organizations}
                        disabled={disabled}
                        onChange={organizations => onChange({organizations})}
                    />
                    <StringListInput
                        label="Entitlements"
                        description="Entitlement tags required to see this template. Blank = no entitlement requirement."
                        addButtonText="Add Entitlement"
                        values={value.entitlements}
                        disabled={disabled}
                        onChange={entitlements => onChange({entitlements})}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default TemplateVisibilityFields;
